import { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Heart, Users, MapPin, Phone, CreditCard, Loader2, CheckCircle, Calendar, Shirt, ShoppingCart, Star, Plus, Trash2, Shield, AlertCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const JACKSONVILLE_EVENT_IMAGE = "https://customer-assets.emergentagent.com/job_1d50465d-a7fd-4247-9524-de4b017856ee/artifacts/maizv55h_IMG_0895.jpeg";

export default function Events() {
  // Stop the Bleed / Medical Response Team State
  const [isSubmittingBleed, setIsSubmittingBleed] = useState(false);
  const [bleedForm, setBleedForm] = useState({ name: '', email: '', phone: '', role: '', notes: '' });
  const [bleedAttendees, setBleedAttendees] = useState([{ name: '' }]);
  const [bleedSuccess, setBleedSuccess] = useState(false);

  // Jacksonville Back to School Bash State
  const [isSubmittingJax, setIsSubmittingJax] = useState(false);
  const [jaxForm, setJaxForm] = useState({ name: '', email: '', phone: '', notes: '' });
  const [jaxAdults, setJaxAdults] = useState([{ name: '' }]);
  const [jaxYouth, setJaxYouth] = useState([{ name: '', age: '' }]);
  const [jaxSuccess, setJaxSuccess] = useState(false);

  // Stop the Bleed handlers
  const addBleedAttendee = () => setBleedAttendees([...bleedAttendees, { name: '' }]);
  const removeBleedAttendee = (index) => { if (bleedAttendees.length > 1) setBleedAttendees(bleedAttendees.filter((_, i) => i !== index)); };
  const updateBleedAttendee = (index, value) => { const updated = [...bleedAttendees]; updated[index].name = value; setBleedAttendees(updated); };

  const bleedAttendeeCount = bleedAttendees.filter(a => a.name.trim()).length;
  const bleedTotalPrice = bleedAttendeeCount * 25;

  const handleBleedSubmit = async (e) => {
    e.preventDefault();
    if (bleedAttendeeCount === 0) { toast.error('Please add at least one attendee'); return; }
    if (!bleedForm.role) { toast.error('Please select your role'); return; }
    setIsSubmittingBleed(true);
    const attendeeDetails = bleedAttendees.filter(a => a.name.trim()).map((a, i) => `Attendee ${i + 1}: ${a.name}`).join('\n');
    try {
      await axios.post(`${API}/contact`, {
        name: bleedForm.name, email: bleedForm.email, phone: bleedForm.phone,
        subject: `Stop the Bleed Training - August 15 - ${bleedAttendeeCount} Attendee(s) - ${bleedForm.role}`,
        message: `MEDICAL RESPONSE TEAM / USHERS TRAINING - STOP THE BLEED

Event: Stop the Bleed Certification + CPR & First Aid Demo
Date: Saturday, August 15, 2026
Sessions: 10:00 AM (CPR/First Aid) & 12:00 PM (Stop the Bleed)

CONTACT INFORMATION:
Name: ${bleedForm.name}
Email: ${bleedForm.email}
Phone: ${bleedForm.phone}
Role: ${bleedForm.role}

ATTENDEES (${bleedAttendeeCount} × $25 = $${bleedTotalPrice}):
${attendeeDetails}

TOTAL: $${bleedTotalPrice}

Includes:
- Stop the Bleed Certification
- CPR & First Aid Demonstration
- All Supplies Included

Notes: ${bleedForm.notes || 'None'}

PAYMENT DUE: August 10`
      });
      const response = await axios.post(`${API}/payments/create-link`, {
        booking_id: `bleed-${Date.now()}`, booking_type: 'stop-the-bleed', amount: bleedTotalPrice,
        description: `Stop the Bleed Training - ${bleedAttendeeCount} Attendee(s)`,
        customer_name: bleedForm.name, customer_email: bleedForm.email
      });
      if (response.data?.payment_link_url) { toast.success('Redirecting to payment...'); window.open(response.data.payment_link_url, '_blank'); }
      setBleedSuccess(true);
    } catch (error) { toast.error('Could not process registration. Please call 912-388-6305'); }
    finally { setIsSubmittingBleed(false); }
  };

  // Jacksonville handlers
  const addJaxAdult = () => setJaxAdults([...jaxAdults, { name: '' }]);
  const removeJaxAdult = (index) => { if (jaxAdults.length > 0) setJaxAdults(jaxAdults.filter((_, i) => i !== index)); };
  const updateJaxAdult = (index, value) => { const updated = [...jaxAdults]; updated[index].name = value; setJaxAdults(updated); };
  const addJaxYouth = () => setJaxYouth([...jaxYouth, { name: '', age: '' }]);
  const removeJaxYouth = (index) => { if (jaxYouth.length > 0) setJaxYouth(jaxYouth.filter((_, i) => i !== index)); };
  const updateJaxYouth = (index, field, value) => { const updated = [...jaxYouth]; updated[index][field] = value; setJaxYouth(updated); };

  const jaxAdultCount = jaxAdults.filter(a => a.name.trim()).length;
  const jaxYouthCount = jaxYouth.filter(y => y.name.trim()).length;
  const jaxTotalPrice = jaxAdultCount * 50;

  const handleJaxSubmit = async (e) => {
    e.preventDefault();
    if (jaxAdultCount === 0 && jaxYouthCount === 0) { toast.error('Please add at least one adult or youth'); return; }
    setIsSubmittingJax(true);
    const adultDetails = jaxAdults.filter(a => a.name.trim()).map((a, i) => `Adult ${i + 1}: ${a.name}`).join('\n');
    const youthDetails = jaxYouth.filter(y => y.name.trim()).map((y, i) => `Youth ${i + 1}: ${y.name} (Age: ${y.age})`).join('\n');
    try {
      await axios.post(`${API}/contact`, {
        name: jaxForm.name, email: jaxForm.email, phone: jaxForm.phone,
        subject: `Back to School Bash - Jacksonville FL - ${jaxAdultCount} Adults, ${jaxYouthCount} Youth`,
        message: `BACK TO SCHOOL BASH REGISTRATION\nDate: August 1st, 2026\nLocation: NFEI Lane Location, Jacksonville, FL\n\nContact: ${jaxForm.name}\nEmail: ${jaxForm.email}\nPhone: ${jaxForm.phone}\n\nADULTS (${jaxAdultCount} × $50 = $${jaxTotalPrice}):\n${adultDetails || 'None'}\n\nYOUTH (${jaxYouthCount} × FREE):\n${youthDetails || 'None'}\n\nTOTAL: $${jaxTotalPrice}`
      });
      if (jaxTotalPrice > 0) {
        const response = await axios.post(`${API}/payments/create-link`, {
          booking_id: `jax-bash-${Date.now()}`, booking_type: 'jax-event', amount: jaxTotalPrice,
          description: `Back to School Bash - ${jaxAdultCount} Adult(s)`, customer_name: jaxForm.name, customer_email: jaxForm.email
        });
        if (response.data?.payment_link_url) { toast.success('Redirecting to payment...'); window.open(response.data.payment_link_url, '_blank'); }
      } else { toast.success('Youth registration successful!'); }
      setJaxSuccess(true);
    } catch (error) { toast.error('Could not process registration. Please call 904-465-5065'); }
    finally { setIsSubmittingJax(false); }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50">
      <section className="py-8 md:py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="text-center mb-8">
            <h1 className="text-4xl md:text-5xl font-black text-[#2a2873] mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Upcoming <span className="bg-gradient-to-r from-[#c31c31] to-[#5a2b8a] bg-clip-text text-transparent">Events</span>
            </h1>
            <p className="text-xl text-[#5a2b8a]/70">Life-saving training events for all ages!</p>
          </motion.div>

          <Tabs defaultValue="stopthebleed" className="w-full">
            <TabsList className="grid w-full grid-cols-2 mb-8 h-16">
              <TabsTrigger value="stopthebleed" className="flex flex-col data-[state=active]:bg-gradient-to-r data-[state=active]:from-red-600 data-[state=active]:to-red-700 data-[state=active]:text-white rounded-l-full h-full" data-testid="bleed-tab">
                <span className="font-bold flex items-center gap-1"><Shield className="w-4 h-4" /> Stop the Bleed</span>
                <span className="text-xs opacity-80">Aug 15 • $25</span>
              </TabsTrigger>
              <TabsTrigger value="jacksonville" className="flex flex-col data-[state=active]:bg-gradient-to-r data-[state=active]:from-blue-600 data-[state=active]:to-indigo-600 data-[state=active]:text-white rounded-r-full h-full" data-testid="jax-tab">
                <span className="font-bold flex items-center gap-1"><Star className="w-4 h-4" /> Back to School</span>
                <span className="text-xs opacity-80">Jacksonville • Aug 1</span>
              </TabsTrigger>
            </TabsList>

            {/* STOP THE BLEED / MEDICAL RESPONSE TEAM TAB */}
            <TabsContent value="stopthebleed">
              <div className="bg-gradient-to-br from-red-50 via-white to-blue-50 rounded-3xl p-6 md:p-8">
                <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} className="bg-gradient-to-r from-red-600 to-red-700 text-white rounded-2xl p-6 mb-8 text-center">
                  <div className="flex items-center justify-center gap-3 mb-2">
                    <Shield className="w-10 h-10" />
                    <Badge className="bg-white/20 text-white text-lg px-4 py-1">STOP THE BLEED® CERTIFICATION</Badge>
                  </div>
                  <p className="text-3xl md:text-4xl font-black">TRAINED TODAY. PREPARED TO SAVE LIVES!</p>
                  <p className="text-xl text-white/90 mt-2">Medical Response Team / Ushers / Volunteers</p>
                </motion.div>

                {/* Urgency Banner */}
                <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.2 }} className="bg-yellow-100 border-2 border-yellow-400 rounded-xl p-4 mb-6 flex items-center justify-center gap-3">
                  <AlertCircle className="w-6 h-6 text-yellow-600" />
                  <p className="text-yellow-800 font-bold">SPACES ARE LIMITED! Payment due by August 10 • Headcount needed by July 13</p>
                </motion.div>

                <div className="grid lg:grid-cols-2 gap-8 items-start">
                  {/* Left Column - Event Info */}
                  <motion.div initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.2 }}>
                    {/* Event Details Card */}
                    <div className="bg-white rounded-xl p-6 shadow-lg border-2 border-red-200 mb-6">
                      <h3 className="text-xl font-bold text-red-800 mb-4 flex items-center gap-2">
                        <Calendar className="w-5 h-5" /> Event Details
                      </h3>
                      <div className="space-y-3 text-gray-700">
                        <p className="flex items-center gap-2">
                          <Calendar className="w-4 h-4 text-red-500" />
                          <strong>Date:</strong> Saturday, August 15, 2026
                        </p>
                        <p className="flex items-center gap-2">
                          <Users className="w-4 h-4 text-red-500" />
                          <strong>For:</strong> Medical Response Team, Ushers, Volunteers
                        </p>
                      </div>

                      {/* Two Sessions */}
                      <div className="grid grid-cols-2 gap-4 mt-6">
                        <div className="bg-blue-50 rounded-xl p-4 border-2 border-blue-200 text-center">
                          <Badge className="bg-blue-600 text-white mb-2">FIRST SESSION</Badge>
                          <p className="text-3xl font-black text-blue-700">10:00 AM</p>
                          <p className="font-bold text-blue-800">CPR & First Aid</p>
                          <p className="text-sm text-blue-600">Demonstration</p>
                        </div>
                        <div className="bg-red-50 rounded-xl p-4 border-2 border-red-200 text-center">
                          <Badge className="bg-red-600 text-white mb-2">SECOND SESSION</Badge>
                          <p className="text-3xl font-black text-red-700">12:00 PM</p>
                          <p className="font-bold text-red-800">Stop the Bleed</p>
                          <p className="text-sm text-red-600">Trauma Certification</p>
                        </div>
                      </div>
                    </div>

                    {/* What's Included */}
                    <div className="bg-white rounded-xl p-6 shadow-lg border-2 border-green-200 mb-6">
                      <h3 className="text-xl font-bold text-green-800 mb-4 flex items-center gap-2">
                        <CheckCircle className="w-5 h-5" /> What's Included
                      </h3>
                      <ul className="space-y-2">
                        <li className="flex items-center gap-2 text-green-700">
                          <CheckCircle className="w-5 h-5 text-green-500" /> Stop the Bleed® Certification
                        </li>
                        <li className="flex items-center gap-2 text-green-700">
                          <CheckCircle className="w-5 h-5 text-green-500" /> CPR & First Aid Demonstration
                        </li>
                        <li className="flex items-center gap-2 text-green-700">
                          <CheckCircle className="w-5 h-5 text-green-500" /> All Supplies Included for Everyone
                        </li>
                      </ul>
                      <div className="mt-4 pt-4 border-t border-green-200 text-center">
                        <p className="text-green-600 font-semibold">ONE PRICE. TWO SKILLS. ONE PURPOSE.</p>
                        <p className="text-2xl font-black text-green-700">SAVE LIVES!</p>
                      </div>
                    </div>

                    {/* Price */}
                    <div className="bg-gradient-to-r from-red-600 to-red-700 rounded-xl p-6 text-white text-center">
                      <p className="text-lg">Registration Fee</p>
                      <p className="text-5xl font-black">$25</p>
                      <p className="text-white/80">per person</p>
                    </div>
                  </motion.div>

                  {/* Right Column - Registration Form */}
                  <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.3 }}>
                    {bleedSuccess ? (
                      <Card className="bg-gradient-to-br from-green-50 to-emerald-50 border-green-300 shadow-xl">
                        <CardContent className="p-8 text-center">
                          <CheckCircle className="w-20 h-20 text-green-500 mx-auto mb-4" />
                          <h2 className="text-2xl font-bold text-green-800 mb-2">Registration Complete!</h2>
                          <p className="text-green-700 mb-4">You're registered for Stop the Bleed training!</p>
                          <Button onClick={() => setBleedSuccess(false)} className="bg-green-500 hover:bg-green-600">Register More Attendees</Button>
                        </CardContent>
                      </Card>
                    ) : (
                      <Card className="bg-white/95 border-red-300 border-2 shadow-xl">
                        <CardHeader className="bg-gradient-to-r from-red-600 to-red-700 text-white pb-4">
                          <CardTitle className="text-2xl"><Shield className="w-6 h-6 inline mr-2" />Register Now</CardTitle>
                          <CardDescription className="text-white/90">$25 per person • Supplies Included</CardDescription>
                        </CardHeader>
                        <CardContent className="p-6">
                          <form onSubmit={handleBleedSubmit} className="space-y-5">
                            {/* Contact Info */}
                            <div className="bg-slate-50 p-4 rounded-xl border">
                              <h4 className="font-bold text-slate-800 mb-3"><Users className="w-5 h-5 inline mr-1" />Contact Information</h4>
                              <div className="space-y-3">
                                <div><Label>Your Name *</Label><Input required value={bleedForm.name} onChange={e => setBleedForm({...bleedForm, name: e.target.value})} placeholder="Full name" className="mt-1" /></div>
                                <div className="grid grid-cols-2 gap-3">
                                  <div><Label>Email *</Label><Input type="email" required value={bleedForm.email} onChange={e => setBleedForm({...bleedForm, email: e.target.value})} placeholder="your@email.com" className="mt-1" /></div>
                                  <div><Label>Phone *</Label><Input type="tel" required value={bleedForm.phone} onChange={e => setBleedForm({...bleedForm, phone: e.target.value})} placeholder="Phone" className="mt-1" /></div>
                                </div>
                                <div>
                                  <Label>Your Role *</Label>
                                  <Select value={bleedForm.role} onValueChange={v => setBleedForm({...bleedForm, role: v})}>
                                    <SelectTrigger className="mt-1"><SelectValue placeholder="Select your role" /></SelectTrigger>
                                    <SelectContent>
                                      <SelectItem value="Medical Response Team">Medical Response Team</SelectItem>
                                      <SelectItem value="Usher">Usher</SelectItem>
                                      <SelectItem value="Volunteer">Volunteer</SelectItem>
                                      <SelectItem value="Other">Other</SelectItem>
                                    </SelectContent>
                                  </Select>
                                </div>
                              </div>
                            </div>

                            {/* Attendees */}
                            <div className="space-y-3">
                              <Label className="font-bold text-red-700 text-lg"><Users className="w-5 h-5 inline mr-1" />Attendees ($25 each)</Label>
                              {bleedAttendees.map((attendee, index) => (
                                <div key={index} className="flex items-center gap-2">
                                  <div className="w-8 h-8 rounded-full bg-red-600 text-white text-sm flex items-center justify-center font-bold">{index + 1}</div>
                                  <Input value={attendee.name} onChange={e => updateBleedAttendee(index, e.target.value)} placeholder="Attendee's full name" className="flex-1 border-red-200" />
                                  {bleedAttendees.length > 1 && <Button type="button" variant="ghost" size="sm" onClick={() => removeBleedAttendee(index)} className="text-red-500"><Trash2 className="w-4 h-4" /></Button>}
                                </div>
                              ))}
                              <Button type="button" variant="outline" onClick={addBleedAttendee} className="w-full border-dashed border-red-300 text-red-600"><Plus className="w-4 h-4 mr-2" />Add Attendee ($25)</Button>
                            </div>

                            {/* Notes */}
                            <div><Label>Notes (Optional)</Label><Textarea value={bleedForm.notes} onChange={e => setBleedForm({...bleedForm, notes: e.target.value})} placeholder="Any special requests or questions" className="mt-1" rows={2} /></div>

                            {/* Total */}
                            <div className="bg-gradient-to-r from-red-100 to-red-50 rounded-xl p-4 text-center border-2 border-red-300">
                              <p className="text-red-600 text-sm">{bleedAttendeeCount} Attendee(s) × $25</p>
                              <p className="text-4xl font-black text-red-700">${bleedTotalPrice}</p>
                              <p className="text-red-500 text-sm mt-1">Payment due by August 10</p>
                            </div>

                            {/* Merch */}
                            <div className="bg-gradient-to-r from-yellow-50 to-orange-50 rounded-xl p-4 border-2 border-yellow-300">
                              <div className="flex items-center gap-2 mb-3"><Shirt className="w-5 h-5 text-[#c31c31]" /><h4 className="font-bold text-[#2a2873]">Get LifeBreath Merch!</h4></div>
                              <Link to="/shop"><Button variant="outline" className="w-full rounded-full border-2 border-[#c31c31] text-[#c31c31] hover:bg-[#c31c31] hover:text-white text-sm"><ShoppingCart className="w-4 h-4 mr-2" />Shop Merch</Button></Link>
                            </div>

                            <Button type="submit" disabled={isSubmittingBleed || bleedAttendeeCount === 0 || !bleedForm.role} className="w-full bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800 rounded-full py-6 text-lg font-bold">
                              {isSubmittingBleed ? <><Loader2 className="w-5 h-5 mr-2 animate-spin" />Processing...</> : <><CreditCard className="w-5 h-5 mr-2" />Register & Pay ${bleedTotalPrice}</>}
                            </Button>
                            <p className="text-center text-sm text-red-500">Secure payment powered by Square</p>
                          </form>
                        </CardContent>
                      </Card>
                    )}
                    <div className="mt-6 text-center"><a href="tel:912-388-6305" className="text-red-800 font-bold text-lg"><Phone className="w-5 h-5 inline mr-1" />912-388-6305</a></div>
                  </motion.div>
                </div>
              </div>
            </TabsContent>

            {/* JACKSONVILLE TAB */}
            <TabsContent value="jacksonville">
              <div className="bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 rounded-3xl p-6 md:p-8">
                <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-2xl p-6 mb-8 text-center">
                  <p className="text-3xl md:text-4xl font-black">🎒 BACK TO SCHOOL BASH 🎒</p>
                  <p className="text-xl text-white/90 mt-2">August 1st, 2026 • Jacksonville, FL</p>
                </motion.div>

                <div className="grid lg:grid-cols-2 gap-8 items-start">
                  <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} transition={{ delay: 0.2 }}>
                    <img src={JACKSONVILLE_EVENT_IMAGE} alt="Back to School Bash" className="w-full rounded-2xl shadow-2xl shadow-blue-200" />
                    <div className="mt-6 bg-white rounded-xl p-6 shadow-lg border-2 border-blue-300">
                      <h3 className="text-xl font-bold text-blue-800 mb-4"><Calendar className="w-5 h-5 inline mr-2" />Event Details</h3>
                      <p className="text-blue-700"><Calendar className="w-4 h-4 inline mr-1" /><strong>Date:</strong> August 1st, 2026 • 10AM-2PM</p>
                      <p className="text-blue-700"><MapPin className="w-4 h-4 inline mr-1" /><strong>Location:</strong> NFEI Lane, 6803 Arques Rd, Jacksonville, FL</p>
                      <div className="mt-4 pt-4 border-t border-blue-200 grid grid-cols-2 gap-4">
                        <div className="bg-red-50 rounded-lg p-3 text-center border border-red-200"><p className="text-red-800 font-bold">Adults</p><p className="text-3xl font-black text-red-600">$50</p></div>
                        <div className="bg-blue-50 rounded-lg p-3 text-center border border-blue-200"><p className="text-blue-800 font-bold">Youth</p><p className="text-3xl font-black text-blue-600">FREE</p></div>
                      </div>
                    </div>
                  </motion.div>

                  <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.3 }}>
                    {jaxSuccess ? (
                      <Card className="bg-gradient-to-br from-green-50 to-emerald-50 border-green-300 shadow-xl">
                        <CardContent className="p-8 text-center">
                          <CheckCircle className="w-20 h-20 text-green-500 mx-auto mb-4" />
                          <h2 className="text-2xl font-bold text-green-800 mb-2">Registration Complete!</h2>
                          <Button onClick={() => setJaxSuccess(false)} className="bg-green-500 hover:bg-green-600">Register More</Button>
                        </CardContent>
                      </Card>
                    ) : (
                      <Card className="bg-white/95 border-blue-300 border-2 shadow-xl">
                        <CardHeader className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white pb-4">
                          <CardTitle className="text-2xl"><Heart className="w-6 h-6 inline mr-2" />Register Now</CardTitle>
                          <CardDescription className="text-white/90">Adults $50 • Youth FREE</CardDescription>
                        </CardHeader>
                        <CardContent className="p-6">
                          <form onSubmit={handleJaxSubmit} className="space-y-5">
                            <div className="bg-slate-50 p-4 rounded-xl border"><h4 className="font-bold text-slate-800 mb-3"><Users className="w-5 h-5 inline mr-1" />Contact Info</h4>
                              <div className="space-y-3">
                                <div><Label>Name *</Label><Input required value={jaxForm.name} onChange={e => setJaxForm({...jaxForm, name: e.target.value})} className="mt-1" /></div>
                                <div className="grid grid-cols-2 gap-3">
                                  <div><Label>Email *</Label><Input type="email" required value={jaxForm.email} onChange={e => setJaxForm({...jaxForm, email: e.target.value})} className="mt-1" /></div>
                                  <div><Label>Phone *</Label><Input type="tel" required value={jaxForm.phone} onChange={e => setJaxForm({...jaxForm, phone: e.target.value})} className="mt-1" /></div>
                                </div>
                              </div>
                            </div>
                            <div className="space-y-3">
                              <Label className="font-bold text-red-700 text-lg"><Users className="w-5 h-5 inline mr-1" />Adults ($50 each)</Label>
                              {jaxAdults.map((adult, index) => (
                                <div key={index} className="flex items-center gap-2">
                                  <div className="w-8 h-8 rounded-full bg-red-500 text-white text-sm flex items-center justify-center font-bold">{index + 1}</div>
                                  <Input value={adult.name} onChange={e => updateJaxAdult(index, e.target.value)} placeholder="Adult's name" className="flex-1 border-red-200" />
                                  {jaxAdults.length > 1 && <Button type="button" variant="ghost" size="sm" onClick={() => removeJaxAdult(index)} className="text-red-500"><Trash2 className="w-4 h-4" /></Button>}
                                </div>
                              ))}
                              <Button type="button" variant="outline" onClick={addJaxAdult} className="w-full border-dashed border-red-300 text-red-600"><Plus className="w-4 h-4 mr-2" />Add Adult ($50)</Button>
                            </div>
                            <div className="space-y-3">
                              <Label className="font-bold text-blue-700 text-lg"><Heart className="w-5 h-5 inline mr-1" />Youth (FREE!)</Label>
                              {jaxYouth.map((y, index) => (
                                <div key={index} className="bg-blue-50 p-3 rounded-lg border border-blue-200">
                                  <div className="flex items-center justify-between mb-2">
                                    <span className="text-blue-800 font-medium text-sm">Youth {index + 1}</span>
                                    {jaxYouth.length > 1 && <Button type="button" variant="ghost" size="sm" onClick={() => removeJaxYouth(index)} className="text-red-500 h-6 w-6 p-0"><Trash2 className="w-3 h-3" /></Button>}
                                  </div>
                                  <div className="grid grid-cols-3 gap-2">
                                    <Input value={y.name} onChange={e => updateJaxYouth(index, 'name', e.target.value)} placeholder="Name" className="col-span-2 border-blue-200 text-sm" />
                                    <Input type="number" min="5" max="18" value={y.age} onChange={e => updateJaxYouth(index, 'age', e.target.value)} placeholder="Age" className="border-blue-200 text-sm" />
                                  </div>
                                </div>
                              ))}
                              <Button type="button" variant="outline" onClick={addJaxYouth} className="w-full border-dashed border-blue-300 text-blue-600"><Plus className="w-4 h-4 mr-2" />Add Youth (FREE)</Button>
                            </div>
                            <div className="bg-blue-100 rounded-xl p-4 border-2 border-blue-300">
                              <div className="grid grid-cols-2 gap-4 text-center mb-3">
                                <div><p className="text-red-600 text-sm">{jaxAdultCount} Adult(s)</p><p className="text-2xl font-black text-red-700">${jaxAdultCount * 50}</p></div>
                                <div><p className="text-blue-600 text-sm">{jaxYouthCount} Youth</p><p className="text-2xl font-black text-blue-700">FREE</p></div>
                              </div>
                              <div className="text-center pt-3 border-t border-blue-300"><p className="text-4xl font-black text-blue-900">${jaxTotalPrice}</p></div>
                            </div>
                            <div className="bg-gradient-to-r from-yellow-50 to-orange-50 rounded-xl p-4 border-2 border-yellow-300">
                              <div className="flex items-center gap-2 mb-3"><Shirt className="w-5 h-5 text-[#c31c31]" /><h4 className="font-bold text-[#2a2873]">Get LifeBreath Merch!</h4></div>
                              <Link to="/shop"><Button variant="outline" className="w-full rounded-full border-2 border-[#c31c31] text-[#c31c31] hover:bg-[#c31c31] hover:text-white text-sm"><ShoppingCart className="w-4 h-4 mr-2" />Shop Merch</Button></Link>
                            </div>
                            <Button type="submit" disabled={isSubmittingJax || (jaxAdultCount === 0 && jaxYouthCount === 0)} className="w-full bg-gradient-to-r from-blue-600 to-indigo-600 rounded-full py-6 text-lg font-bold">
                              {isSubmittingJax ? <><Loader2 className="w-5 h-5 mr-2 animate-spin" />Processing...</> : jaxTotalPrice > 0 ? <><CreditCard className="w-5 h-5 mr-2" />Register & Pay ${jaxTotalPrice}</> : <><CheckCircle className="w-5 h-5 mr-2" />Register Youth (FREE)</>}
                            </Button>
                          </form>
                        </CardContent>
                      </Card>
                    )}
                    <div className="mt-6 text-center"><a href="tel:904-465-5065" className="text-blue-800 font-bold text-lg"><Phone className="w-5 h-5 inline mr-1" />904-465-5065</a></div>
                  </motion.div>
                </div>
              </div>
            </TabsContent>
          </Tabs>
        </div>
      </section>
    </div>
  );
}
